import React, { useState, useEffect, memo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Loader2 } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { api } from "../../App";
import { LoadingOverlay } from "../LoadingOverlay";

const HobliApplicationsSummary = memo(({ onHobliClick, department }) => {
  const { t } = useTranslation();
  const [hobliStats, setHobliStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clickedHobli, setClickedHobli] = useState(null);

  useEffect(() => {
    const fetchHobliStats = async () => {
      try {
        setLoading(true);
        const params = department && department !== "all" ? `?department=${department}` : "";
        const response = await api.get(`/dashboard/hobli-stats${params}`);
        setHobliStats(response.data);
      } catch (error) {
        console.error("Error fetching hobli stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHobliStats();
  }, [department]);

  const handleHobliClick = (hobli) => {
    setClickedHobli(hobli);
    // Call parent callback immediately
    if (onHobliClick) {
      onHobliClick(hobli);
    }
    // Reset clicked state after a delay
    setTimeout(() => setClickedHobli(null), 2000);
  };

  if (loading) {
    return <LoadingOverlay fullPage={false} />;
  }

  if (!hobliStats) return null;

  const hobliOrder = ["Kasaba", "Kudur", "Madabalu", "Tippasandra"];

  return (
    <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 mb-4" data-testid="hobli-summary-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          {t('dashboard.applicationsPerHobli') || 'Applications per Hobli'}
        </h3>
        <Badge variant="outline" className="bg-white text-blue-700 border-blue-300">
          {t('officialsManagement.total') || 'Total'}: {hobliStats.total}
        </Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {hobliOrder.map((hobli) => (
          <button
            key={hobli}
            onClick={() => handleHobliClick(hobli)}
            disabled={clickedHobli !== null}
            className={`flex flex-col items-center p-3 bg-white rounded-lg border transition-all cursor-pointer group ${
              clickedHobli === hobli 
                ? 'border-blue-500 shadow-md bg-blue-50' 
                : 'border-blue-100 hover:border-blue-300 hover:shadow-sm'
            }`}
            data-testid={`hobli-card-${hobli.toLowerCase()}`}
          >
            {clickedHobli === hobli ? (
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            ) : (
              <span className="text-2xl font-bold text-indigo-600 group-hover:text-indigo-700">
                {hobliStats.hoblis[hobli] || 0}
              </span>
            )}
            <span className="text-xs text-slate-600 mt-1">
              {t(`hobli.${hobli.toLowerCase()}`) || hobli}
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
});

HobliApplicationsSummary.displayName = 'HobliApplicationsSummary';

export default HobliApplicationsSummary;
